import { gql } from '@apollo/client';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { PendingAchEulaError } from 'components/data/eulas/PendingAchEulas.js';
import useStdQuery from 'components/data/hooks/useStdQuery.js';
import LoadingIcon from 'components/icons/LoadingIcon.js';
import { useRefetchIfOld } from 'components/data/ServerTime.js';
import PublicAppVars from 'utils/PublicAppVars.js';

export const GET_EULAS_SEARCH_IDS = gql`
	query EulaSearch (
		$status: String
		$channels: [ String! ]
		$features: [ String! ]
		$eulaType: String
		$addLocaleInfo: Boolean
		$publishEndDate: GraphQLDateTime
	){
		eulas {
			search(
				status: $status
				channels: $channels
				features: $features
				eulaType: $eulaType
				addLocaleInfo: $addLocaleInfo
				publishEndDate: $publishEndDate
			) {
				nodeTime
				eulaSearchList {
					id
					eulaId
					feature
					name
					effectiveDate
					archive
					featureDescription
				}
			}
		}
	}
`;

// BO Eula Features
// https://reflexions.atlassian.net/browse/MBTA-2583
export const NEW_ACH = "NewACHFundingSource";
export const NEW_REGISTERED_USER = "NewRegisteredUser";
export const NEW_TRAVELER_AUTOLOAD = "NewTravelerAutoload";
export const NEW_TRAVELER_ALTERNATE_USER = "NewTravelerAlternateUser";

export const EulaFeatures = {
	NEW_ACH,
	NEW_REGISTERED_USER,
	NEW_TRAVELER_AUTOLOAD,
	NEW_TRAVELER_ALTERNATE_USER,
};

export const useEulaSearch = ({
	status = "Active",
	channels,
	features,
	eulaType,
	loading,
	addLocaleInfo = false,
	publishEndDate,
}) => {
	const response = useStdQuery(GET_EULAS_SEARCH_IDS, {
		variables: {
			status,
			channels,
			features,
			eulaType,
			addLocaleInfo,
			publishEndDate,
		},
		loading,
	});

	const { data, loading: loadingResponse, refetch } = response;
	const queryTime = data?.eulas.search.nodeTime;

	useRefetchIfOld({ refetch, loading: loadingResponse, queryTime });

	const eulas = useMemo(() => data?.eulas.search.eulaSearchList ?? [], [ data?.eulas.search ]);

	return { eulas, response };
};

const EulaSearch = ({
	children,
	status = 'Active',
	channels = PublicAppVars.EULA_CHANNELS,
	features,
	eulaType,
	loading,
	renderError = null,
}) => {

	const { eulas, response } = useEulaSearch({
		status,
		channels,
		features,
		eulaType,
		loading,
	});

	const { loading: loadingResponse, error } = response;

	if (loadingResponse) {
		return (<LoadingIcon />);
	}

	if (error) {
		return (<>{renderError || <PendingAchEulaError />}</>);
	}

	return (<>
		{children(eulas, response)}
	</>);
};

EulaSearch.propTypes = {
	children: PropTypes.func.isRequired,
	status: PropTypes.string,
	channels: PropTypes.arrayOf(PropTypes.string),
	features: PropTypes.oneOf(Object.values(EulaFeatures)),
	idsOnly: PropTypes.bool,
	eulaType: PropTypes.string,
};

export default EulaSearch;
